// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Flex,
  useToast,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect } from "react";
import {
  MdAttachMoney,
  MdSchool,
  MdPayment,
  MdPendingActions,
  MdCheckCircle,
} from "react-icons/md";
import { formatCurrency, formatNumber } from "utils/format";
import dashboardService from "services/dashboardService";
import tuitionService from "services/tuitionService";
import { getCurrentUser } from "services/authService";
import { USER_ROLES } from "constants/roles";
import TuitionsTable from "./components/TuitionsTable";

export default function TuitionManagement() {
  // Chakra Color Mode - All hooks must be called at the top level
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const toast = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [tuitions, setTuitions] = useState([]);
  const [tuitionsLoading, setTuitionsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [resettingOverdue, setResettingOverdue] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
  });
  const isCenterAdmin = getCurrentUser()?.role === USER_ROLES.CENTER_ADMIN;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fetch financial data
  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (isCenterAdmin) {
          const [tuitionsResponse, pendingResponse, validatedResponse, rejectedResponse] =
            await Promise.all([
              tuitionService.getTuitions({ limit: 100 }),
              tuitionService.getPayments({ status: 'pending', limit: 100 }),
              tuitionService.getPayments({ status: 'validated', limit: 100 }),
              tuitionService.getPayments({ status: 'rejected', limit: 100 }),
            ]);

          const failed = [tuitionsResponse, pendingResponse, validatedResponse, rejectedResponse].find(
            (result) => !result.success,
          );

          if (failed) {
            setError(failed.error || 'Failed to load tuition data');
            return;
          }

          const tuitionsData = tuitionsResponse.data || [];
          const pendingPayments = pendingResponse.data || [];
          const validatedPayments = validatedResponse.data || [];

          setFinancialData({
            total_tuitions: tuitionsResponse.pagination?.total || tuitionsData.length,
            total_tuition_amount: tuitionsData.reduce((sum, tuition) => sum + Number(tuition.amount || 0), 0),
            total_paid: validatedPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
            total_pending: pendingPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
            total_validated_payments: validatedPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
            payments_by_status: {
              pending: pendingResponse.pagination?.total || pendingPayments.length,
              validated: validatedResponse.pagination?.total || validatedPayments.length,
              rejected: rejectedResponse.pagination?.total || rejectedResponse.data?.length || 0,
            },
          });
          return;
        }

        const response = await dashboardService.getFinancialStats();
        
        if (response.success) {
          setFinancialData(response.data);
        } else {
          setError(response.error || 'Failed to load tuition data');
        }
      } catch (err) {
        console.error('Tuition fetch error:', err);
        setError(err.message || 'Failed to load tuition data');
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, [isCenterAdmin, refreshKey]);

  // Fetch tuitions data
  useEffect(() => {
    const fetchTuitions = async () => {
      try {
        setTuitionsLoading(true);
        const skip = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const response = await tuitionService.getTuitions({
          skip: skip,
          limit: pagination.itemsPerPage,
          search: debouncedSearchQuery || undefined,
        });
        
        if (response.success && response.data) {
          setTuitions(response.data);
          if (response.pagination) {
            setPagination(prev => ({
              ...prev,
              total: response.pagination.total || 0,
            }));
          }
        }
      } catch (err) {
        console.error('Fetch tuitions error:', err);
      } finally {
        setTuitionsLoading(false);
      }
    };

    fetchTuitions();
  }, [pagination.currentPage, pagination.itemsPerPage, debouncedSearchQuery, refreshKey]);

  const handleResetOverdueToPending = async () => {
    const confirmed = window.confirm(
      'Set all overdue tuition records back to pending? This will update every overdue tuition you have access to.',
    );

    if (!confirmed) return;

    setResettingOverdue(true);
    try {
      const result = await tuitionService.resetOverdueToPending();

      if (result.success) {
        toast({
          title: 'Overdue tuitions updated',
          description: result.message || 'All overdue tuitions were set to pending.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        setRefreshKey((prev) => prev + 1);
      } else {
        toast({
          title: 'Update failed',
          description: result.error || 'Could not reset overdue tuitions.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    } finally {
      setResettingOverdue(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!financialData) {
    return null;
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Page Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="12px"
        mb="20px"
      >
        <Text fontSize="2xl" fontWeight="700" color={textColor}>
          Tuition Management
        </Text>
        <Button
          colorScheme="orange"
          leftIcon={<Icon as={MdPendingActions} />}
          onClick={handleResetOverdueToPending}
          isLoading={resettingOverdue}
          loadingText="Updating"
          alignSelf={{ base: "stretch", md: "auto" }}
        >
          Set Overdue to Pending
        </Button>
      </Flex>

      {/* Statistics Cards */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdSchool} color={brandColor} />
              }
            />
          }
          name='Total Tuitions'
          value={formatNumber(financialData?.total_tuitions || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdAttachMoney} color={brandColor} />
              }
            />
          }
          name='Total Tuition Amount'
          value={formatCurrency(financialData?.total_tuition_amount || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPayment} color={brandColor} />
              }
            />
          }
          name='Total Paid'
          value={formatCurrency(financialData?.total_paid || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPendingActions} color={brandColor} />
              }
            />
          }
          name='Pending Payments'
          value={formatCurrency(financialData?.total_pending || 0)}
        />
      </SimpleGrid>

      {/* Additional Statistics */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdCheckCircle} color={brandColor} />
              }
            />
          }
          name='Validated Payments'
          value={formatCurrency(financialData?.total_validated_payments || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPendingActions} color={brandColor} />
              }
            />
          }
          name='Pending Payments Count'
          value={formatNumber(financialData?.payments_by_status?.pending || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdCheckCircle} color={brandColor} />
              }
            />
          }
          name='Validated Payments Count'
          value={formatNumber(financialData?.payments_by_status?.validated || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPayment} color={brandColor} />
              }
            />
          }
          name='Rejected Payments'
          value={formatNumber(financialData?.payments_by_status?.rejected || 0)}
        />
      </SimpleGrid>

      {/* Tuitions Table */}
      <Box mt="40px">
        {tuitionsLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
            <Spinner size="lg" color="brand.500" />
          </Box>
        ) : (
          <TuitionsTable 
            tableData={tuitions} 
            pagination={pagination}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
            onItemsPerPageChange={(itemsPerPage) => setPagination(prev => ({ ...prev, itemsPerPage, currentPage: 1 }))}
          />
        )}
      </Box>
    </Box>
  );
}
